import { Dropdown } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import { MODES_REGLEMENTS_POINTAGE } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import { useReglementPointage } from '@/hooks';
import type { ModeReglementsPointageType } from '@/types';

type PointageModeReglementProps = {
  modeReglementActif: ModeReglementsPointageType;
  disabled: boolean;
  loading: boolean;
  getTotalByCode: (code: ModeReglementsPointageType) => number;
  onModeReglementChange: (event: unknown, data: Record<string, any>) => void;
  /**
   * Contrôle optionnel inséré entre le dropdown de mode de règlement et le compteur
   * (« X règlements pour Y reçus »). Utilisé par l'arrêté périodique pour le sélecteur de
   * période. Absent ailleurs (ex. pointage de bordereaux) → aucun changement.
   */
  periodeSelector?: React.ReactNode;
};

const PointageModeReglement = (props: PointageModeReglementProps) => {
  const {
    modeReglementActif,
    disabled,
    loading,
    getTotalByCode,
    onModeReglementChange,
    periodeSelector,
  } = props;

  const { reglements } = useReglementPointage(modeReglementActif);

  const { regieModesReglement } = React.useContext(ModeReglementContext);

  // Calcul du nombre de reçus valides et annulés
  const { reglementsValide, recusValides, recusAnnules } = React.useMemo(() => {
    const rglmtsValide = _.filter(reglements, (r) => _.isNil(r.dateAnnulationRecu));
    return {
      reglementsValide: _.size(rglmtsValide),
      recusValides: _.size(_.uniqBy(rglmtsValide, 'identifiantRecu')),
      recusAnnules: _.size(
        _.uniqBy(
          _.filter(reglements, (r) => !_.isNil(r.dateAnnulationRecu)),
          'identifiantRecu',
        ),
      ),
    };
  }, [reglements]);

  const modeReglementsOptions = React.useMemo(
    () =>
      _.map(
        _.pickBy(
          MODES_REGLEMENTS_POINTAGE,
          (_mode, code) =>
            _.some(regieModesReglement, { code }) &&
            (loading || getTotalByCode(code as ModeReglementsPointageType) > 0),
        ),
        (mode, code) => ({
          key: code,
          text: mode,
          value: code,
          description: (
            <b
              title={`${getTotalByCode(code as ModeReglementsPointageType)} règlement${getTotalByCode(code as ModeReglementsPointageType) > 1 ? 's' : ''} à contrôler pour ce mode de règlement`}
            >
              {getTotalByCode(code as ModeReglementsPointageType).toString()}
            </b>
          ),
        }),
      ),
    [regieModesReglement, getTotalByCode, loading],
  );

  // Si le mode actif disparaît des options après chargement (count = 0),
  // sélectionner automatiquement le premier mode disponible.
  React.useEffect(() => {
    if (loading) return;
    const isActiveInOptions = modeReglementsOptions.some((o) => o.value === modeReglementActif);
    if (!isActiveInOptions && modeReglementsOptions.length > 0) {
      onModeReglementChange(null, { value: modeReglementsOptions[0].value });
    }
  }, [loading, modeReglementsOptions, modeReglementActif, onModeReglementChange]);

  return (
    <div>
      <Dropdown
        selection
        disabled={disabled}
        loading={loading}
        options={modeReglementsOptions}
        defaultValue={modeReglementActif}
        value={modeReglementActif}
        onChange={onModeReglementChange}
        closeOnChange
        openOnFocus
        style={{ marginLeft: '1em', marginRight: '1em' }}
      />
      {periodeSelector}
      {!loading && (recusValides > 0 || recusAnnules > 0) && (
        <span style={{ fontWeight: 'bold' }}>
          {`${reglementsValide} règlement${reglementsValide > 1 ? 's' : ''} pour ${recusValides} reçu${recusValides > 1 ? 's' : ''}`}
          <span style={{ fontWeight: 'normal', fontStyle: 'italic', color: 'red' }}>
            {recusAnnules > 0 &&
              ` - ${recusAnnules} reçu${recusAnnules > 1 ? 's' : ''} annulé${recusAnnules > 1 ? 's' : ''}`}
          </span>
        </span>
      )}
    </div>
  );
};

export default PointageModeReglement;
